/**
 * LIVEMOA · BARCODE/WMS A4-1D · Supplier picker (매입처 선택).
 *
 * Shared control used by:
 *   - 재고조정 modal (AdjustModal · 입고 사유일 때만)
 *   - 입고 스캔 modal (InboundScanModal · 세션 상단)
 *
 * REUSES the pre-existing `GET /api/seller/suppliers` + `POST /api/seller/suppliers`
 * endpoints (Purchase Orders module).  Does NOT duplicate the supplier engine —
 * this is a thin picker only.  No supplier is EVER auto-selected.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown, Plus, X, Check } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

export default function SupplierPicker({
  value, onChange, testId = 'supplier-picker', disabled = false,
  compact = false,
}) {
  const [suppliers, setSuppliers] = useState([]);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [loading, setLoading] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await api.get('/seller/suppliers');
      setSuppliers(r.data?.items || []);
    } catch { /* silent · picker is optional */ }
    finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const selected = useMemo(
    () => suppliers.find((s) => s.supplier_id === value) || null,
    [suppliers, value]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return suppliers.slice(0, 20);
    return suppliers.filter((s) => (
      (s.name || '').toLowerCase().includes(needle)
      || (s.contact_name || '').toLowerCase().includes(needle)
      || (s.phone || '').includes(needle)
    )).slice(0, 20);
  }, [suppliers, q]);

  return (
    <div className={`relative ${compact ? '' : ''}`} data-testid={testId}>
      <button type="button" onClick={() => !disabled && setOpen((v) => !v)}
              disabled={disabled}
              className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg border text-xs
                          border-black/10 dark:border-white/10 bg-transparent
                          ${disabled ? 'opacity-40 cursor-not-allowed' : 'hover:bg-black/5 dark:hover:bg-white/5'}`}
              data-testid={`${testId}-open`}>
        <span className={`truncate ${!selected ? 'text-gray-400' : 'font-bold'}`}>
          {selected
            ? `${selected.name}${selected.contact_name ? ` · ${selected.contact_name}` : ''}`
            : '매입처 선택 (선택 사항)'}
        </span>
        <span className="flex items-center gap-1">
          {selected && (
            <span onClick={(e) => { e.stopPropagation(); onChange(null); }}
                  className="p-0.5 rounded hover:bg-black/10" data-testid={`${testId}-clear`}>
              <X size={11} />
            </span>
          )}
          <ChevronDown size={12} className="opacity-60" />
        </span>
      </button>
      {open && !disabled && (
        <div className="absolute z-50 mt-1 w-full rounded-xl border border-black/10 dark:border-white/10 bg-white dark:bg-[#141414] shadow-2xl p-2 max-h-64 overflow-y-auto"
             data-testid={`${testId}-dropdown`}>
          <input value={q} onChange={(e) => setQ(e.target.value)} autoFocus
                 placeholder="매입처명 · 담당자 · 전화번호 검색"
                 className="w-full px-2 py-1.5 text-xs rounded border border-black/10 dark:border-white/10 bg-transparent mb-1"
                 data-testid={`${testId}-search`}/>
          {loading ? (
            <p className="text-[11px] text-gray-500 py-3 text-center">불러오는 중…</p>
          ) : filtered.length === 0 ? (
            <p className="text-[11px] text-gray-500 py-3 text-center">
              {q ? '검색 결과가 없습니다.' : '등록된 매입처가 없습니다. 신규 등록 →'}
            </p>
          ) : (
            <div className="space-y-0.5">
              {filtered.map((s) => (
                <button key={s.supplier_id} type="button"
                        onClick={() => { onChange(s.supplier_id); setOpen(false); }}
                        className={`w-full text-left px-2 py-1.5 rounded text-xs hover:bg-black/5 dark:hover:bg-white/10 flex items-center justify-between ${
                          s.supplier_id === value ? 'bg-black/5 dark:bg-white/10 font-bold' : ''}`}
                        data-testid={`${testId}-option-${s.supplier_id}`}>
                  <span className="truncate">
                    <b>{s.name}</b>
                    {(s.contact_name || s.phone) && (
                      <span className="text-gray-500 ml-1">
                        · {[s.contact_name, s.phone].filter(Boolean).join(' · ')}
                      </span>
                    )}
                  </span>
                  {s.supplier_id === value && <Check size={11} className="text-[#FF3B30]" />}
                </button>
              ))}
            </div>
          )}
          <div className="border-t border-black/10 dark:border-white/10 mt-1 pt-1">
            <button type="button" onClick={() => { setShowCreate(true); setOpen(false); }}
                    className="w-full text-left px-2 py-1.5 rounded text-xs text-[#FF3B30] font-black hover:bg-red-500/10 flex items-center gap-1"
                    data-testid={`${testId}-add-new`}>
              <Plus size={11}/> 신규 매입처 등록
            </button>
          </div>
        </div>
      )}
      {showCreate && (
        <SupplierCreateModal
          onClose={() => setShowCreate(false)}
          onCreated={async (sup) => {
            setShowCreate(false);
            await reload();
            if (sup?.supplier_id) onChange(sup.supplier_id);
          }}
        />
      )}
    </div>
  );
}

// A4-1D · 매입처 등록 · A4-1D 단순화 필드 (MOQ/lead/발주단위/안전재고 제거).
function SupplierCreateModal({ onClose, onCreated }) {
  const [f, setF] = useState({
    name: '', contact_name: '', phone: '', kakao_id: '',
    email: '', address: '', address_detail: '', memo: '',
  });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.name.trim()) { toast.error('매입처명을 입력해주세요.'); return; }
    setBusy(true);
    try {
      const r = await api.post('/seller/suppliers', f);
      toast.success('매입처가 등록되었습니다.');
      onCreated(r.data?.supplier);
    } catch (e) { toast.error(e?.response?.data?.detail || '등록 실패'); }
    finally { setBusy(false); }
  };
  const fields = [
    ['name', '매입처명 *'],
    ['contact_name', '담당자명'],
    ['phone', '전화번호'],
    ['kakao_id', '카카오톡 연락처/ID'],
    ['email', '이메일'],
    ['address', '주소'],
    ['address_detail', '상세주소'],
    ['memo', '메모'],
  ];
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="supplier-create-modal-a4d">
      <div className="absolute inset-0 bg-black/60" onClick={onClose}/>
      <div className="relative bg-white dark:bg-[#0A0A0A] rounded-2xl shadow-2xl w-full max-w-md overflow-hidden max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b border-black/10 dark:border-white/10 flex justify-between items-center">
          <p className="text-sm font-black">매입처 등록</p>
          <button onClick={onClose} data-testid="supplier-create-close"><X size={14}/></button>
        </div>
        <div className="p-4 space-y-2">
          {fields.map(([k, l]) => (
            <div key={k}>
              <p className="text-[10px] font-black uppercase text-gray-500 mb-0.5">{l}</p>
              <input value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })}
                     className="w-full px-3 py-2 text-sm rounded-lg border border-black/10 dark:border-white/10 bg-transparent"
                     data-testid={`supplier-create-${k}`}/>
            </div>
          ))}
          <button onClick={submit} disabled={busy}
                  className="w-full py-2.5 rounded-full bg-[#FF3B30] text-white text-sm font-black disabled:opacity-40"
                  data-testid="supplier-create-submit">
            {busy ? '등록 중…' : '매입처 등록'}
          </button>
        </div>
      </div>
    </div>
  );
}
